import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { fetchDebtors, sendDebtReminder, sendDebtRemindersToAll } from "@/api/finance";
import { formatDate, formatMoney } from "@/utils/format";

function downloadCsv(rows: { studentName: string; phone: string; branchName: string; debt: number; overdueDays: number }[]) {
  const header = ["ФИО", "Телефон", "Филиал", "Долг", "Дней просрочки"];
  const lines = [header.join(";"), ...rows.map((r) => [r.studentName, r.phone, r.branchName, r.debt, r.overdueDays].join(";"))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `debtors_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function DebtsTab() {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["debtors"],
    queryFn: () => fetchDebtors({ limit: 200 }),
  });

  const remindMutation = useMutation({
    mutationFn: (studentId: string) => sendDebtReminder(studentId),
    onSuccess: () => setSnackbar("Напоминание поставлено в очередь отправки."),
  });

  const remindAllMutation = useMutation({
    mutationFn: () => sendDebtRemindersToAll(),
    onSuccess: (result) => {
      setSnackbar(`В очередь поставлено ${result.queued} напоминаний.`);
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const rows = data?.items ?? [];
  const totalDebt = rows.reduce((sum, r) => sum + r.debt, 0);
  const overdueRows = rows.filter((r) => r.overdueDays > 0);
  const overdueDebt = overdueRows.reduce((sum, r) => sum + r.debt, 0);
  const uniqueDebtors = new Set(rows.map((r) => r.studentId)).size;
  const maxOverdue = overdueRows.slice().sort((a, b) => b.overdueDays - a.overdueDays)[0];

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Общая задолженность" value={formatMoney(totalDebt)} hint={`${rows.length} начислений`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Просрочено" value={formatMoney(overdueDebt)} hint={`${overdueRows.length} позиций`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Должников" value={String(uniqueDebtors)} hint="уникальных учеников" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Максимальная просрочка" value={maxOverdue ? `${maxOverdue.overdueDays} дн` : "0 дн"} hint={maxOverdue?.studentName ?? "—"} />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<NotificationsActiveIcon />}
            disabled={remindAllMutation.isPending}
            onClick={() => remindAllMutation.mutate()}
          >
            Напомнить всем
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(rows)}>
            Экспорт CSV
          </Button>
        </Stack>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ученик</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Филиал</TableCell>
              <TableCell>Долг</TableCell>
              <TableCell>Срок</TableCell>
              <TableCell>Дней просрочки</TableCell>
              <TableCell align="right">Действие</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.studentId}>
                <TableCell>{row.studentName}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>{row.branchName}</TableCell>
                <TableCell>{formatMoney(row.debt)}</TableCell>
                <TableCell>{formatDate(row.oldestDueDate)}</TableCell>
                <TableCell>{row.overdueDays}</TableCell>
                <TableCell align="right">
                  <Button size="small" disabled={remindMutation.isPending} onClick={() => remindMutation.mutate(row.studentId)}>
                    Напомнить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Должников нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar} />
    </>
  );
}
